import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  keepPreviousData,
  type InfiniteData,
} from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import {
  acceptSurfaceAsset,
  bulkSurfaceAction,
  fetchSurfaceFeed,
  fetchSurfaceProgress,
  fetchSurfaceSolutionTags,
  rejectSurfaceAsset,
  reprocessSurfaceAsset,
  skipSurfaceAsset,
} from "./promoteSurface.service";
import { promoteSurfaceKeys } from "./promoteSurface.query-keys";
import type {
  AcceptSurfaceAssetBody,
  SurfaceBulkBody,
  SurfaceFeedResponse,
  SurfaceProgress,
  SurfaceTab,
  SurfaceWriteAction,
} from "./types";
import { verificationStatusForTab } from "./types";
import { invalidateMarketingAssets } from "@/react/components/entityViews/marketing/api/marketingAssets.cache";

const CONCURRENCY = 8;

function setProgressCache(slug: string, progress: SurfaceProgress | undefined) {
  if (!progress) return;
  queryClient.setQueryData(promoteSurfaceKeys.progress(slug), progress);
}

async function invalidateSurfaceCaches(slug: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: promoteSurfaceKeys.board(slug) }),
  ]);
}

export { invalidateSurfaceCaches };

export function useSurfaceProgressQuery(slug: string | undefined, enabled = true) {
  return useQuery({
    queryKey: promoteSurfaceKeys.progress(slug ?? ""),
    queryFn: () => fetchSurfaceProgress(slug!),
    enabled: !!slug && enabled,
    refetchInterval: 1000,
    refetchIntervalInBackground: false,
  });
}

export function useSurfaceFeedInfiniteQuery(
  slug: string | undefined,
  tab: SurfaceTab,
  enabled = true
) {
  return useInfiniteQuery({
    queryKey: promoteSurfaceKeys.feed(slug ?? "", tab),
    initialPageParam: undefined as string | number | undefined,
    queryFn: async ({ pageParam }) => {
      const page = await fetchSurfaceFeed(slug!, {
        verificationStatus: verificationStatusForTab(tab),
        cursor: pageParam,
      });
      setProgressCache(slug!, page.progress);
      return page;
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!slug && enabled,
    // Keep prior tab tiles while the next tab feed loads (avoids empty flash).
    placeholderData: keepPreviousData,
    // Feed refreshes on mutation invalidate; light poll catches new AI drafts
    // without doubling the 1s progress traffic.
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
  });
}

export function useSurfaceSolutionTagsQuery(slug: string | undefined, enabled = true) {
  return useQuery({
    queryKey: promoteSurfaceKeys.solutionTags(slug ?? ""),
    queryFn: () => fetchSurfaceSolutionTags(slug!),
    enabled: !!slug && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function flattenFeedAssets(
  data: InfiniteData<SurfaceFeedResponse> | undefined
) {
  return data?.pages.flatMap((p) => p.assets) ?? [];
}

function getAxiosMessage(error: unknown): string {
  const err = error as {
    response?: { data?: { detail?: string; message?: string } };
    message?: string;
  };
  return (
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.message ||
    "Request failed"
  );
}

type SurfaceWritePayload = {
  action: SurfaceWriteAction;
  assetId: number;
  body?: AcceptSurfaceAssetBody;
};

/** Single write path for live single and bulk fan-out. */
async function executeSurfaceWrite(slug: string, input: SurfaceWritePayload) {
  switch (input.action) {
    case "accept":
      return acceptSurfaceAsset(slug, input.assetId, input.body ?? { tag_ids: [] });
    case "reject":
      return rejectSurfaceAsset(slug, input.assetId);
    case "skip":
      return skipSurfaceAsset(slug, input.assetId);
    case "reprocess":
      return reprocessSurfaceAsset(slug, input.assetId);
  }
}

export function useSurfaceAssetMutations(
  slug: string | undefined,
  boardId?: number | null,
) {
  const runSingle = useMutation({
    mutationFn: async (input: {
      action: SurfaceWriteAction;
      assetId: number;
      body?: AcceptSurfaceAssetBody;
      skipInvalidate?: boolean;
    }) => {
      if (!slug) throw new Error("Missing board slug");
      return executeSurfaceWrite(slug, input);
    },
    onSuccess: async (_data, variables) => {
      if (slug && !variables.skipInvalidate) await invalidateSurfaceCaches(slug);
      // Accept/reject/skip change which assets belong on the marketing tile.
      if (
        boardId != null &&
        (variables.action === "accept" ||
          variables.action === "reject" ||
          variables.action === "skip")
      ) {
        await invalidateMarketingAssets(boardId);
      }
    },
  });

  const runBulk = useMutation({
    mutationFn: async (input: SurfaceBulkBody) => {
      if (!slug) throw new Error("Missing board slug");
      try {
        return await bulkSurfaceAction(slug, input);
      } catch {
        return fanOutBulk(slug, input);
      }
    },
    onSuccess: async (_data, variables) => {
      if (slug) await invalidateSurfaceCaches(slug);
      if (
        boardId != null &&
        (variables.action === "accept" ||
          variables.action === "reject" ||
          variables.action === "skip")
      ) {
        await invalidateMarketingAssets(boardId);
      }
    },
  });

  return {
    runSingle,
    runBulk,
    getAxiosMessage,
    invalidateSurfaceCaches: () => slug && invalidateSurfaceCaches(slug),
  };
}

async function fanOutBulk(slug: string, body: SurfaceBulkBody) {
  const ids = body.asset_ids;
  const results: Array<{
    asset_id: number;
    ok: boolean;
    error?: string;
  }> = [];

  for (let i = 0; i < ids.length; i += CONCURRENCY) {
    const chunk = ids.slice(i, i + CONCURRENCY);
    const settled = await Promise.allSettled(
      chunk.map((assetId) =>
        executeSurfaceWrite(slug, {
          action: body.action,
          assetId,
          body: {
            tag_ids: body.tag_ids ?? [],
            caption: body.caption,
            alt_text: body.alt_text,
            is_restricted: body.is_restricted,
            restriction_reason: body.restriction_reason,
          },
        }).then(() => assetId)
      )
    );

    settled.forEach((result, idx) => {
      const assetId = chunk[idx];
      if (result.status === "fulfilled") {
        results.push({ asset_id: assetId, ok: true });
      } else {
        results.push({
          asset_id: assetId,
          ok: false,
          error: getAxiosMessage(result.reason),
        });
      }
    });
  }

  return { results };
}

export { getAxiosMessage };
